import React, { useState } from 'react';
import { Col, Row, Collapse, Card, CardBody, Input } from 'reactstrap';
import styled from 'styled-components';
import { Colors, OutputRow, ButtonContainer, CollapseButton, CollapseRow, BoldLabel, HighlightedInput } from '../../../../utils/budget-tool-specific';
import { RebelColors } from '../../../../styles/rebel-experiences';
import { displayValue } from '../../helper';
import { calculateContingency, calculateCostTrip, calculateCostMeals, calculateCostAccommodation, calculateCostActivity, calculateCostGroundTransport, calculateCostMeetings, calculateCostMiscellaneous, calculateCostFlight, calculateInternalCostA, calculateInternalCostB, calculateCostPerAttendee } from '../../experiences/cost-summary/cost-helper';
import TallyDisplay from '../components/tally-display';
import RankingDropdown from '../components/ranking-dropdown';


const RebelSummaryExperienceRow = ({
    guideData,
    expData,
    experienceId,
    client = false,
    allRankings = {},
    selectedRankings = {},
    onRankingChange,
    totalExperiences = 0,
    setIndex,
    clientCurrency
}) => {

    const normalStyle = {
        transition: '1s, transform 0.5s',
    };

    const transformedStyle = {
        transition: '1s, transform 0.5s',
        transform: 'rotate(180deg)',
    };

    const [isOpen, setIsOpen] = useState(false);

    // Calculate margin with proper attendee multiplication
    const calculateMarginCost = () => {
        if (guideData.marginPercent === undefined || guideData.marginPercent.value === true) {
            // Percentage-based margin (existing calculation)
            return calculateInternalCostB(guideData, expData, clientCurrency);
        } else {
            // Dollar-based margin - multiply by number of attendees
            return parseFloat(guideData.margin.value) * guideData.attendees.value;
        }
    };

    // Calculate total trip cost with corrected margin
    const calculateTotalTripCost = () => {
        const baseCost = calculateCostPerAttendee(guideData, expData) * guideData.attendees.value;
        const overheadCost = calculateInternalCostA(guideData, expData, clientCurrency);
        const marginCost = calculateMarginCost();
        return baseCost + overheadCost + marginCost;
    };

    return (
        <React.Fragment>
            <UniformRow>
                <TallyCol lg="1" xs="1">
                    <ChevronButton onClick={() => {setIsOpen(!isOpen)}}>
                        <RebelCollapseButton icon={['fas', 'chevron-down']} style={isOpen ? transformedStyle : normalStyle}/>
                    </ChevronButton>
                    <TallyDisplay
                        allRankings={allRankings}
                        experienceId={experienceId}
                        displayMode="average"
                    />
                </TallyCol>
                <TextCol>
                    <RebelText>{expData.title.value}</RebelText>
                </TextCol>
                <TextCol lg="2">
                    <RebelText>
                        {guideData.currency.value.slice(3)}{displayValue(calculateTotalTripCost() / guideData.attendees.value)}
                    </RebelText>
                </TextCol>
                <TextCol lg="2">
                    <RebelText>
                        {guideData.currency.value.slice(3)}{displayValue(calculateTotalTripCost())}
                    </RebelText>
                </TextCol>
                {client && (
                    <RankingCol lg="1" xs="1">
                        <RankingDropdown
                            experienceId={experienceId}
                            selectedRankings={selectedRankings}
                            onChange={onRankingChange}
                            totalExperiences={totalExperiences}
                        />
                    </RankingCol>
                )}
                <Col lg="1">
                </Col>
            </UniformRow>
            <Collapse isOpen={isOpen}>
                <CollapseRow>
                    <Col>
                        <RebelCard>
                        <PaddedCardBody>
                            <Row style={{display: "flex", alignItems: "flex-end"}}>
                                <Col lg="1"></Col>
                                <Col>
                                    <RebelBoldLabel>
                                        Categories
                                    </RebelBoldLabel>
                                </Col>
                                <Col lg="2">
                                    <RebelBoldLabel>
                                        Cost Per Attendee
                                    </RebelBoldLabel>
                                </Col>
                                <Col lg="2">
                                    <RebelBoldLabel>
                                        Cost for Trip
                                    </RebelBoldLabel>
                                </Col>
                            </Row>
                            <Row>
                                <Col lg="1"></Col>
                                <Col>
                                    <RebelText>Villa Rental & Cleaning</RebelText>
                                </Col>
                                <Col>
                                    {expData && <React.Fragment>
                                        {expData.villaRentalFee?.value > 0 &&
                                            <React.Fragment>
                                                <RebelText>&gt; Villa Rental: </RebelText>
                                                <RebelText>{guideData.currency.value.slice(3)}{displayValue(expData.villaRentalFee.value)}</RebelText>
                                            </React.Fragment>}
                                        {expData.villaRentalFee?.value > 0 && expData.cleaningFee?.value > 0 && <br />}
                                        {expData.cleaningFee?.value > 0 &&
                                            <React.Fragment>
                                                <RebelText>&gt; Cleaning Fee: </RebelText>
                                                <RebelText>{guideData.currency.value.slice(3)}{displayValue(expData.cleaningFee.value)}</RebelText>
                                            </React.Fragment>}
                                    </React.Fragment>}
                                </Col>
                                <Col lg="2">
                                    <RebelText>
                                        {guideData.currency.value.slice(3)}{displayValue(calculateCostAccommodation(guideData, expData))}
                                    </RebelText>
                                </Col>
                                <Col lg="2">
                                    <RebelText>
                                        {guideData.currency.value.slice(3)}{displayValue(calculateCostAccommodation(guideData, expData) * guideData.attendees.value)}
                                    </RebelText>
                                </Col>
                            </Row>
                            <Row>
                                <Col lg="1"></Col>
                                <Col>
                                    <RebelText>Flights</RebelText>
                                </Col>
                                <Col lg="2">
                                    <RebelText>
                                        {guideData.currency.value.slice(3)}{displayValue(calculateCostFlight(guideData, expData))}
                                    </RebelText>
                                </Col>
                                <Col lg="2">
                                    <RebelText>
                                        {guideData.currency.value.slice(3)}{displayValue(calculateCostFlight(guideData, expData) * guideData.attendees.value)}
                                    </RebelText>
                                </Col>
                            </Row>
                            <Row>
                            <Col lg="1"></Col>
                                <Col>
                                    <RebelText>Activities</RebelText>
                                </Col>
                                <Col lg="2">
                                    <RebelText>
                                        {guideData.currency.value.slice(3)}{displayValue(calculateCostActivity(guideData, expData))}
                                    </RebelText>
                                </Col>
                                <Col lg="2">
                                    <RebelText>
                                        {guideData.currency.value.slice(3)}{displayValue(calculateCostActivity(guideData, expData) * guideData.attendees.value)}
                                    </RebelText>
                                </Col>
                            </Row>
                            {expData.transportation.value !== "" && <Row>
                                <Col lg="1"></Col>
                                <Col>
                                    <RebelText>Ground Transportation</RebelText>
                                </Col>
                                <Col lg="2">
                                    <RebelText>
                                        {guideData.currency.value.slice(3)}{displayValue(calculateCostGroundTransport(guideData, expData))}
                                    </RebelText>
                                </Col>
                                <Col lg="2">
                                    <RebelText>
                                        {guideData.currency.value.slice(3)}{displayValue(calculateCostGroundTransport(guideData,expData) * guideData.attendees.value)}
                                    </RebelText>
                                </Col>
                            </Row>}
                            <Row>
                            <Col lg="1"></Col>
                                <Col>
                                    <RebelText>Meals</RebelText>
                                </Col>
                                <Col lg="2">
                                    <RebelText>
                                        {guideData.currency.value.slice(3)}{displayValue(calculateCostMeals(guideData, expData))}
                                    </RebelText>
                                </Col>
                                <Col lg="2">
                                    <RebelText>
                                        {guideData.currency.value.slice(3)}{displayValue(calculateCostMeals(guideData, expData) * guideData.attendees.value)}
                                    </RebelText>
                                </Col>
                            </Row>
                            <Row>
                            <Col lg="1"></Col>
                                <Col>
                                    <RebelText>Meetings</RebelText>
                                </Col>
                                <Col lg="2">
                                    <RebelText>
                                        {guideData.currency.value.slice(3)}{displayValue(calculateCostMeetings(guideData, expData))}
                                    </RebelText>
                                </Col>
                                <Col lg="2">
                                    <RebelText>
                                        {guideData.currency.value.slice(3)}{displayValue(calculateCostMeetings(guideData, expData) * guideData.attendees.value)}
                                    </RebelText>
                                </Col>
                            </Row>
                            <Row>
                            <Col lg="1"></Col>
                                <Col>
                                    <RebelText>Miscellaneous Costs</RebelText>
                                </Col>
                                <Col lg="2">
                                    <RebelText>
                                        {guideData.currency.value.slice(3)}{displayValue(calculateCostMiscellaneous(guideData, expData))}
                                    </RebelText>
                                </Col>
                                <Col lg="2">
                                    <RebelText>
                                        {guideData.currency.value.slice(3)}{displayValue(calculateCostMiscellaneous(guideData, expData) * guideData.attendees.value)}
                                    </RebelText>
                                </Col>
                            </Row>
                            <Row>
                            <Col lg="1"></Col>
                                <Col>
                                    <RebelText>Contingency</RebelText>
                                </Col>
                                <Col lg="2">
                                    <RebelText>
                                        {guideData.currency.value.slice(3)}{displayValue(calculateContingency(guideData, expData))}
                                    </RebelText>
                                </Col>
                                <Col lg="2">
                                    <RebelText>
                                        {guideData.currency.value.slice(3)}{displayValue(calculateContingency(guideData, expData) * guideData.attendees.value)}
                                    </RebelText>
                                </Col>
                            </Row>
                            <Row>
                            <Col lg="1"></Col>
                                <Col>
                                    <RebelText>
                                        {(guideData.overheadPercent === undefined || guideData.margin.value !== "0") ? "Administrative & Management Fee" : "Moniker's Management Fee"}
                                    </RebelText>
                                </Col>
                                <Col lg="2">
                                </Col>
                                <Col lg="2">
                                    <RebelText>
                                        {guideData.currency.value.slice(3)}{displayValue(calculateInternalCostA(guideData, expData))}
                                    </RebelText>
                                </Col>
                            </Row>
                            {guideData.margin.value !== "0" &&<Row>
                                <Col lg="1"></Col>
                                <Col>
                                    <RebelText>Planning Fee</RebelText>
                                </Col>
                                <Col lg="2">

                                </Col>
                                <Col lg="2">
                                    <RebelText>
                                        {guideData.currency.value.slice(3)}{displayValue(calculateMarginCost())}
                                    </RebelText>
                                </Col>
                            </Row>}
                        </PaddedCardBody>
                        </RebelCard>
                    </Col>
                </CollapseRow>
            </Collapse>
        </React.Fragment>
    )
}

export default RebelSummaryExperienceRow;

const RebelCard = styled(Card)`
    background-color: ${RebelColors.surface};
    border: none;
`

const PaddedCardBody = styled(CardBody)`
    padding: 15px 0px;
`

const RebelCollapseButton = styled(CollapseButton)`
    color: ${RebelColors.primary};
`

const RebelBoldLabel = styled(BoldLabel)`
    color: ${RebelColors.gold};
`

const UniformRow = styled(OutputRow)`
    display: flex;
    align-items: center;
    min-height: 48px;
    padding: 8px 0;
`;

const RebelText = styled.p`
    color: ${RebelColors.primary};
    margin: 0;
    padding: 8px 0;
    line-height: 1.4;
`

const RebelButtonContainer = styled(ButtonContainer)`
    &:hover {
        background-color: #000000;
    }
`

const TallyCol = styled(Col)`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding-left: 5px !important;
    padding-right: 5px !important;
`;

const TextCol = styled(Col)`
    display: flex;
    align-items: center;
`;

const RankingCol = styled(Col)`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ChevronButton = styled.div`
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px;
    border-radius: 4px;
    transition: background-color 0.3s ease;
    height: 32px;
    width: 32px;

    &:hover {
        background-color: #000000;
    }
`
